import { useState, useEffect, useMemo, useRef } from 'react';
import { Calendar as CalendarIcon } from 'lucide-react';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Popover, PopoverContent, PopoverTrigger } from '@/components/ui/popover';
import { Calendar } from '@/components/ui/calendar';
import { cn } from '@/lib/utils';
import InputMask from 'react-input-mask';
import * as React from 'react';
import { useTranslation } from 'react-i18next';
import { namespace } from '@common-config/constants';
import { hoverWash } from './picker-colors';

interface BTDatePickerProps {
  value?: string; // Format: dd/mm/yyyy
  onChange?: (value: string) => void; // Format: dd/mm/yyyy
  primaryColor?: string; // Primary color for border and hover (default: #3b82f6 blue)
  lightColor?: string; // Light color for today/selected background (default: #e0f2fe light blue)
  darkColor?: string; // Dark color for text and selected border (default: #0369a1 dark blue)
  /** Ink ON the `lightColor` fill (the selected day). Defaults to `darkColor`,
   *  which is right for a fixed pair of literals — but a THEMED caller must pass
   *  its own: in dark mode the accent fill and the primary ink are the same sky
   *  blue, so reusing `darkColor` paints the selected day's number invisible. */
  onLightColor?: string;
  disabled?: (date: Date) => boolean; // Function to disable specific dates
  zIndex?: number; // Z-index for the popover content
  /** Custom popover trigger. When supplied, the bordered input pill is NOT
   *  rendered — this element becomes the sole trigger and the calendar anchors
   *  to it (`PopoverTrigger asChild`, so it must forward ref + props; a native
   *  <button> does). Used by the compose dialog's summary line, where the date
   *  is an inline clickable word rather than a field. Typed selection is not
   *  available in this mode; the calendar is. */
  trigger?: React.ReactNode;
  /** BE#657 — when false, the picker does NOT emit `onChange(today)` on mount for an
   *  empty value. The scheduled-compose pickers pass false so a fresh draft keeps an
   *  empty dueDate (Send stays gated) instead of being silently seeded to "now", which
   *  could fire an unfinished message early. Other callers (bulk/campaign tabs) keep the
   *  default (true) so their now-default is preserved. The field still DISPLAYS today as
   *  a calendar anchor; it is just not committed until the user picks. */
  seedDefaultOnMount?: boolean;
}

// Get default date (current date in dd/mm/yyyy format)
const getDefaultDate = (): string => {
  const now = new Date();
  const day = String(now.getDate()).padStart(2, '0');
  const month = String(now.getMonth() + 1).padStart(2, '0');
  const year = now.getFullYear();
  return `${day}/${month}/${year}`;
};

export default function BTDatePicker({ 
  value = '', 
  onChange,
  primaryColor = '#3b82f6', // Default blue for border
  lightColor = '#e0f2fe', // Default light blue for calendar backgrounds
  darkColor = '#0369a1', // Default dark blue for calendar text and borders
  onLightColor = darkColor,
  disabled,
  zIndex = 20050, // Default high z-index to appear above dialogs
  trigger,
  seedDefaultOnMount = true
}: BTDatePickerProps) {
  const { t } = useTranslation(namespace);
  const [open, setOpen] = useState(false);
  const hasInitializedRef = useRef(false);

  // Get default date only once on mount
  const defaultDate = useMemo(() => getDefaultDate(), []);
  const displayValue = value || defaultDate;
  const [inputValue, setInputValue] = useState(displayValue);

  // Initialize with default if no value provided (only once on mount)
  useEffect(() => {
    if (!seedDefaultOnMount) return; // BE#657 — compose pickers opt out of now-seeding
    if (!hasInitializedRef.current && !value && onChange) {
      hasInitializedRef.current = true;
      onChange(defaultDate);
    }
  }, [value, onChange, defaultDate, seedDefaultOnMount]);

  // Sync with external value changes
  useEffect(() => {
    setInputValue(value || defaultDate);
    if (value) {
      hasInitializedRef.current = true;
    }
  }, [value, defaultDate]);

  // Get days in a month
  const getDaysInMonth = (month: number, year: number): number => {
    return new Date(year, month, 0).getDate();
  };

  // Validate and correct date to nearest valid date
  const correctDate = (dateString: string): string => {
    if (!dateString || dateString.length < 10) return dateString;

    const parts = dateString.split('/');
    if (parts.length !== 3) return dateString;

    let [day, month, year] = parts.map(Number);

    // Validate year (keep as is if reasonable, otherwise use current year)
    if (isNaN(year) || year < 1900 || year > 2100) {
      year = new Date().getFullYear();
    }

    // Correct month (1-12)
    if (isNaN(month) || month < 1) {
      month = 1;
    } else if (month > 12) {
      month = 12;
    }

    // Get max days for the month
    const maxDays = getDaysInMonth(month, year);

    // Correct day
    if (isNaN(day) || day < 1) {
      day = 1;
    } else if (day > maxDays) {
      day = maxDays;
    }

    return formatDate(new Date(year, month - 1, day));
  };

  // Convert dd/mm/yyyy to Date object
  const parseDate = (dateString: string): Date | undefined => {
    if (!dateString) return undefined;
    const parts = dateString.split('/');
    if (parts.length !== 3) return undefined;
    const [day, month, year] = parts.map(Number);
    if (isNaN(day) || isNaN(month) || isNaN(year)) return undefined;
    const date = new Date(year, month - 1, day);
    if (isNaN(date.getTime())) return undefined;
    // Check if the date is valid (e.g., not 32/13/2024)
    if (date.getDate() !== day || date.getMonth() !== month - 1 || date.getFullYear() !== year) {
      return undefined;
    }
    return date;
  };

  // Convert Date object to dd/mm/yyyy
  const formatDate = (date: Date): string => {
    const day = String(date.getDate()).padStart(2, '0');
    const month = String(date.getMonth() + 1).padStart(2, '0');
    const year = date.getFullYear();
    return `${day}/${month}/${year}`;
  };

  const selectedDate = parseDate(inputValue || displayValue);

  const handleCalendarSelect = (date: Date | undefined) => {
    if (!date) return;
    const formatted = formatDate(date);
    setInputValue(formatted);
    onChange?.(formatted);
    setOpen(false);
  };

  const handleInputChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const newValue = e.target.value;
    setInputValue(newValue);

    // Only call onChange when date is complete and valid
    if (newValue.length === 10) {
      const corrected = correctDate(newValue);
      const finalValue = corrected !== newValue ? corrected : newValue;
      const currentDisplayValue = value || inputValue || defaultDate;

      // Only update if different from current displayed value
      if (finalValue !== currentDisplayValue) {
        setInputValue(finalValue);
        const parsed = parseDate(finalValue);
        if (parsed) {
          onChange?.(finalValue);
        }
      }
    }
  };

  const handleInputBlur = () => {
    // When user leaves the input, correct any invalid date
    const currentValue = inputValue || displayValue;
    if (currentValue && currentValue.length === 10) {
      const corrected = correctDate(currentValue);
      if (corrected !== currentValue) {
        setInputValue(corrected);
        onChange?.(corrected);
      }
    }
  };

  // Check if a date is in the past (before today)
  const isPastDate = (date: Date): boolean => {
    const today = new Date();
    today.setHours(0, 0, 0, 0);
    const compareDate = new Date(date);
    compareDate.setHours(0, 0, 0, 0);
    return compareDate < today;
  };

  return (
    // In trigger mode these wrappers are `display:contents` so the button flows
    // INLINE inside the summary sentence (a block box here would stack the date
    // onto its own line and break the one-sentence reading).
    <div style={trigger ? { display: 'contents' } : undefined}>
      <div style={trigger ? { display: 'contents' } : undefined}>
        <Popover open={open} onOpenChange={setOpen}>
          {trigger ? (
            // Summary-line mode: the caller's inline word IS the trigger; no pill.
            <PopoverTrigger asChild>{trigger}</PopoverTrigger>
          ) : (
          <div
            className="flex items-center justify-center w-full max-w-[190px] px-3 h-8 min-h-8 rounded-[20px] bg-transparent border transition-[background-color,border-color] duration-200 border-slate-200 dark:border-slate-700"
            style={{
              '--hover-bg-color': hoverWash(primaryColor)
            } as React.CSSProperties & { '--hover-bg-color': string }}
            onMouseEnter={(e) => {
              e.currentTarget.style.backgroundColor = hoverWash(primaryColor);
              e.currentTarget.style.borderColor = primaryColor;
            }}
            onMouseLeave={(e) => {
              e.currentTarget.style.backgroundColor = 'transparent';
              e.currentTarget.style.borderColor = '';
            }}
          >
            <InputMask
              mask="99/99/9999"
              value={inputValue || displayValue}
              onChange={handleInputChange}
              onBlur={handleInputBlur}
              maskChar=""
            >
              {(inputProps: React.InputHTMLAttributes<HTMLInputElement>) => (
                <Input
                  {...inputProps}
                  type="text"
                  placeholder={t('dateTime.datePlaceholder')}
                  className="h-[30px] w-[110px] text-center text-sm font-medium text-[var(--color-text)] border-0 shadow-none focus-visible:ring-0 focus-visible:ring-offset-0 bg-transparent !bg-transparent px-0 pe-0"
                  style={{ backgroundColor: 'transparent', paddingInlineEnd: 0 }}
                />
              )}
            </InputMask>
            <PopoverTrigger asChild>
              <Button
                variant="ghost"
                size="icon"
                // NOTE: Tailwind's logical `ms-*` compiles to a physical margin-left
                // in this build (verified: identical computed margin in LTR and RTL),
                // so it is NOT an RTL fix here. 2px is below the visual threshold and
                // is left as-is; the direction-agnostic remedy, if it ever matters, is
                // a `gap` on the wrapper rather than a margin on this button.
                className="p-1 w-5 h-6 rounded-[6px] transition-all ml-0.5 text-[var(--color-text)] dark:text-white hover:bg-slate-100 dark:hover:bg-slate-800"
              >
                <CalendarIcon className="h-[18px] w-[18px]" />
              </Button>
            </PopoverTrigger>
          </div>
          )}
          {/* BE#657 dark-mode — the shadcn PopoverContent default sets no background, so the calendar
              must carry the dark-aware surface token itself (same fix as the time picker). */}
          <PopoverContent align="center" className="w-auto p-4 rounded-xl border border-[var(--color-border)] bg-[var(--color-surface)] text-[var(--color-text)]" zIndex={zIndex}>
            <Calendar
              mode="single"
              selected={selectedDate}
              onSelect={handleCalendarSelect}
              disabled={disabled}
              modifiers={{
                past: isPastDate
              }}
              modifiersStyles={{
                past: {
                  color: '#9ca3af',
                  opacity: 0.6
                },
                today: {
                  backgroundColor: 'transparent',
                  color: darkColor,
                  fontWeight: '600',
                  borderRadius: '50%',
                  width: '36px',
                  height: '36px',
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: 'center',
                  border: `2px solid ${lightColor}`
                },
                selected: {
                  backgroundColor: lightColor,
                  color: onLightColor,
                  fontWeight: '600',
                  borderRadius: '50%',
                  width: '36px',
                  height: '36px',
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: 'center',
                  border: `2px solid ${lightColor}`
                }
              }}
              modifiersClassNames={{
                past: 'past-date',
                today: 'today-date'
              }}
              // Removed fromDate and disabled restrictions to allow navigation to past dates
              // Users can now navigate backward to previous months and select past dates
              // Past dates are grayed out but still selectable
              initialFocus
            />
          </PopoverContent>
        </Popover>
      </div>
    </div>
  );
}
